import { Controller } from 'stimulus';
import { useEventListener } from '../../util';

export default class extends Controller {
  static classes = ['item', 'group'];

  connect() {
    useEventListener(this);
    this.addEventListener(document, 'createOutline', this.createOutline.bind(this));
    this.addEventListener(document, 'removeOutline', this.removeOutline.bind(this));
    this.addEventListener(document, 'updateOutline', this.updateOutline.bind(this));
    this.addEventListener(document, 'removeAllOutlines', this.removeAllOutlines.bind(this));
    this.addEventListener(
      document,
      'moodboard--selection-box:stop-selection',
      this.removeAllOutlines.bind(this)
    );

    this.outlinedItems = new Set();
    this.pendingOutlineCreations = [];
    this.pendingOutlineRemovals = [];
    this.flushScheduled = false;
  }

  removeAllOutlines() {
    this.pendingOutlineCreations = [];

    // queued removals have already been dropped from outlinedItems, so the loop below can't
    // reach them - drain the queue here or their paths outlive every future cleanup
    this.pendingOutlineRemovals.forEach((itemId) => {
      const outline = document.querySelector(`[data-outline-reference="${itemId}"]`);
      if (outline) {
        outline.remove();
      }
    });
    this.pendingOutlineRemovals = [];

    this.outlinedItems.forEach((outlineId) => {
      const outline = document.querySelector(`[data-outline-reference="${outlineId}"]`);
      if (outline) {
        outline.remove();
      }
    });
    this.outlinedItems.clear();
  }

  removeOutline(event) {
    if (!this.outlinedItems.has(event.detail.itemId)) {
      return;
    }

    this.outlinedItems.delete(event.detail.itemId);
    this.pendingOutlineRemovals.push(event.detail.itemId);
    this.#scheduleFlush();
  }

  updateOutline(event) {
    const outline = document.querySelector(`[data-outline-reference="${event.detail.itemId}"]`);
    if (!outline) {
      return false;
    }
    outline.setAttribute('d', this.#buildPath(event.detail.itemCorners));
  }

  createOutline(event) {
    if (this.outlinedItems.has(event.detail.itemId)) {
      return;
    }

    this.outlinedItems.add(event.detail.itemId);
    this.pendingOutlineCreations.push(event.detail);
    this.#scheduleFlush();
  }

  #scheduleFlush() {
    if (this.flushScheduled) {
      return;
    }
    this.flushScheduled = true;
    queueMicrotask(() => this.#flushOutlineChanges());
  }

  #flushOutlineChanges() {
    this.flushScheduled = false;

    // Remove outlines
    for (const itemId of this.pendingOutlineRemovals) {
      const outline = document.querySelector(`[data-outline-reference="${itemId}"]`);
      if (outline) {
        outline.remove();
      }
    }

    // Create outlines
    if (this.pendingOutlineCreations.length > 0) {
      const fragment = document.createDocumentFragment();

      for (const detail of this.pendingOutlineCreations) {
        const outline = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        outline.classList.add(...this.#classesFor(detail));
        outline.dataset.outlineReference = detail.itemId;
        outline.setAttributeNS(null, 'd', this.#buildPath(detail.itemCorners));
        fragment.appendChild(outline);
      }

      this.element.appendChild(fragment);
    }

    this.pendingOutlineCreations = [];
    this.pendingOutlineRemovals = [];
  }

  #classesFor({ classes }) {
    if (classes === 'group') {
      return this.groupClasses;
    }
    return classes ? classes.split(' ').filter(Boolean) : this.itemClasses;
  }

  #buildPath(corners) {
    return `M${corners[0][0]} ${corners[0][1]} L${corners[1][0]} ${corners[1][1]} L${corners[2][0]} ${corners[2][1]} L${corners[3][0]} ${corners[3][1]} Z`;
  }
}
